import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sidepanel } from 'components';
import { ContentHeader } from 'screens/Admin/components';
import SubscriptionStateCard from './SubscriptionStateCard';
import RefundStateCard from './RefundStateCard';
import StripeStateCard from './StripeStateCard';

const InfoRow = ({ label, children, muted }) => (
  <div className="flex items-baseline justify-between py-[5px]">
    <span className="text-[12px] text-[#8993a4] select-none">{label}</span>
    <span className={`text-[13px] font-medium ${muted ? 'text-[#8993a4]' : 'text-[#2d3643]'}`}>
      {children}
    </span>
  </div>
);

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-semibold text-[#8993a4] uppercase tracking-[0.06em]">
    {children}
  </p>
);

const formatLabel = (str) =>
  str?.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || '-';

const AuditLogPanel = ({ log, visible, closePanel }) => {
  const { t } = useTranslation();
  const tl = (key) => t(`admin.auditLogs.panel.${key}`);

  if (!log) return null;

  const isRefund = log.entity_type === 'refund';
  const isStripe = log.entity_type === 'stripe_account';
  const StateCard = isStripe ? StripeStateCard : isRefund ? RefundStateCard : SubscriptionStateCard;
  const targetId = isStripe
    ? log.target_stripe_request_id
    : isRefund
      ? log.target_refund_request_id
      : log.target_subscription_id;

  return (
    <Sidepanel wid={600} pad="0" visible={!!visible} closeModal={closePanel}>
      <ContentHeader text={tl('title')} closePanel={closePanel} />

      <div className="px-6 py-5 space-y-5">
        {/* Event Details Card */}
        <div className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden">
          <div className="px-5 py-3 space-y-0.5">
            <InfoRow label={tl('actor')}>{log.actor_id?.name || log.actor_email || '-'}</InfoRow>
            <InfoRow label={tl('target')}>{log.target_user_id?.name || log.target_user_id?.email || '-'}</InfoRow>
            <InfoRow label={tl('action')}>{formatLabel(log.action_type)}</InfoRow>
            <InfoRow label={tl('entityType')}>{formatLabel(log.entity_type)}</InfoRow>
            {targetId && (
              <InfoRow label={isStripe ? 'Stripe Request ID' : isRefund ? tl('refundRequestId') : tl('subscriptionId')} muted>
                {targetId}
              </InfoRow>
            )}
            <InfoRow label={tl('timestamp')} muted>
              {log.createdAt ? new Date(log.createdAt).toLocaleString() : '-'}
            </InfoRow>
            <InfoRow label={tl('ipAddress')}>{log.ip_address || '-'}</InfoRow>
          </div>
        </div>

        {/* Before State Card */}
        <div>
          <SectionLabel>{tl('beforeState')}</SectionLabel>
          <div className="mt-2">
            {log.before_state ? (
              <StateCard state={log.before_state} />
            ) : (
              <p className="text-[13px] text-[#8993a4] italic">{tl('na')}</p>
            )}
          </div>
        </div>

        {/* After State Card */}
        <div>
          <SectionLabel>{tl('afterState')}</SectionLabel>
          <div className="mt-2">
            {log.after_state ? (
              <StateCard state={log.after_state} />
            ) : (
              <p className="text-[13px] text-[#8993a4] italic">{tl('na')}</p>
            )}
          </div>
        </div>
      </div>
    </Sidepanel>
  );
};

export default AuditLogPanel;
